import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Typography } from 'seagull';
import { useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSendOTPCode } from '~/hooks/useSendOTPCode/useSendOTPCode';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import commonStyles from '../ChangeEmailAddress.module.scss';
import { ViewType } from '../ChangeEmailAddressTypes';
import OTPInputSection from '../OTPInputSection';
const EnterOneTimePassword = (param)=>{
    let { onClose, handleSetView } = param;
    const { api } = useStoreActions();
    const { t: emailTexts } = useLocaleKeys('components.changeEmailAddress');
    const { t: errorTexts } = useLocaleKeys('errors');
    const [isLoading, setIsLoading] = useState(false);
    const { sendOTPCode } = useSendOTPCode();
    const { control, handleSubmit, setError, reset, watch, clearErrors } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            otp: ''
        }
    });
    const handleValidatingError = (error)=>{
        if (error.isTooManyAttemptsError()) {
            handleSetView(ViewType.TooManyAttempts);
            return;
        }
        if (error.hasServerError(ServerError.InvalidOTP)) {
            setError('otp', {
                type: 'manual',
                message: emailTexts.invalidCode()
            });
            return;
        }
        setError('otp', {
            type: 'manual',
            message: errorTexts.unexpected()
        });
    };
    const validateExistingOTP = async (param)=>{
        let { otp } = param;
        setIsLoading(true);
        const result = await handleRequest(api.user.verifyOTP({
            otp
        }));
        if (result.success) {
            handleSetView(ViewType.SetUpNewEmail);
        } else {
            handleValidatingError(result.error);
            setIsLoading(false);
        }
    };
    const onSendOTPCode = async (param)=>{
        let { showSuccessToast } = param;
        await sendOTPCode({
            requestOTP: ()=>api.user.getOTPCode(),
            onTooManyRequests: ()=>handleSetView(ViewType.TooManyAttempts),
            showSuccessToast
        });
    };
    useOnMount(()=>{
        onSendOTPCode({
            showSuccessToast: false
        });
    });
    const onResendCode = async ()=>{
        reset();
        await onSendOTPCode({
            showSuccessToast: true
        });
    };
    const otpValue = watch('otp');
    useEffect(()=>{
        clearErrors('otp');
    }, [
        otpValue,
        clearErrors
    ]);
    return /*#__PURE__*/ _jsxs("form", {
        "data-testid": "enter-one-time-password",
        onSubmit: handleSubmit(validateExistingOTP),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: commonStyles.content,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: "one-time-password-message",
                        children: "Please enter the one-time code sent to your registered email."
                    }),
                    /*#__PURE__*/ _jsx(OTPInputSection, {
                        isLoading: isLoading,
                        control: control,
                        resendCode: onResendCode
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: commonStyles.footer,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'close_change_email_otp'
                        },
                        onClick: onClose,
                        skin: "primaryOutline",
                        testId: "close-button",
                        children: "Close"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'submit_change_email_otp'
                        },
                        type: "submit",
                        testId: "continue-button",
                        loading: isLoading,
                        children: "Continue"
                    })
                ]
            })
        ]
    });
};
export default EnterOneTimePassword;
